"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  Loader,
  AlertCircle,
  User,
  BookOpen,
  Bookmark,
  Clock,
  Calendar,
  ChevronRight,
  CheckCircle,
  Filter,
  ChevronDown,
} from "lucide-react"
import { getAllbooks } from "./api"
import { generatePresignedUrl } from "../postFileAws"

const Books = () => {
  const [books, setBooks] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [booksFetched, setBooksFetched] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)
  const [filterSubject, setFilterSubject] = useState("all")
  const [sortOption, setSortOption] = useState("newest")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const navigate = useNavigate()

  const classRoomId = localStorage.getItem("classRoomId")

  const fetchBooks = useCallback(async () => {
    if (booksFetched) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      if (!booksFetched) {
        const response = await getAllbooks(classRoomId)

        if (response.body.data) {
          const booksWithPresignedUrls = await Promise.all(
            response.body.data.map(async (book) => {
              const presignedUrl = await generatePresignedUrl(book.fileUrl)
              return { ...book, presignedUrl }
            }),
          )
          setBooks(booksWithPresignedUrls)
          setBooksFetched(true)
        } else {
          setBooks([])
          setBooksFetched(true)
        }
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }, [booksFetched, classRoomId])

  useEffect(() => {
    fetchBooks()
  }, [fetchBooks])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const handleBookClick = (book) => {
    navigate(`/books/studybuddy/${book._id}`)
  }

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const getUniqueSubjects = () => {
    const subjects = books.map((book) => book.fileSubject)
    return ["all", ...new Set(subjects)]
  }

  const filteredBooks = books
    .filter((book) => {
      const matchesSearch =
        book.fileTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.authorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.fileSubject.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesSubject = filterSubject === "all" || book.fileSubject === filterSubject

      return matchesSearch && matchesSubject
    })
    .sort((a, b) => {
      if (sortOption === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt)
      } else if (sortOption === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt)
      } else if (sortOption === "title") {
        return a.fileTitle.localeCompare(b.fileTitle)
      } else if (sortOption === "author") {
        return a.authorName.localeCompare(b.authorName)
      }
      return 0
    })

  const getFilterLabel = () => {
    if (filterSubject !== "all") {
      return filterSubject.length > 8 ? filterSubject.substring(0, 8) + "..." : filterSubject
    }
    return "All Subjects"
  }

  // Calculate stats for the header
  const totalBooks = books.length
  const totalSubjects = new Set(books.map((book) => book.fileSubject)).size
  const recentBooks = books.filter((book) => {
    const bookDate = new Date(book.createdAt)
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    return bookDate >= weekAgo
  }).length
  const totalAuthors = new Set(books.map((book) => book.authorName)).size

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading your library</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your books...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setBooksFetched(false)
              setError(null)
              fetchBooks()
            }}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl max-w-md animate-fade-in flex items-center ${
            flashMessage.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {flashMessage.type === "success" ? (
            <CheckCircle className="mr-3 flex-shrink-0" size={20} />
          ) : (
            <AlertCircle className="mr-3 flex-shrink-0" size={20} />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Stats, Search, and Filter */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* Stats - Compact for mobile */}
                <div className="flex items-center gap-1 mr-1">
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{totalBooks}</p>
                    <p className="text-white/80 text-[8px]">Books</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{totalSubjects}</p>
                    <p className="text-white/80 text-[8px]">Subjects</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{recentBooks}</p>
                    <p className="text-white/80 text-[8px]">Recent</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center">
                    <p className="text-white text-[10px] font-bold">{totalAuthors}</p>
                    <p className="text-white/80 text-[8px]">Authors</p>
                  </div>
                </div>

                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {getUniqueSubjects().map((subject) => (
                        <button
                          key={subject}
                          onClick={() => {
                            setFilterSubject(subject)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left transition-colors hover:bg-gray-50 ${
                            filterSubject === subject ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {subject === "all" ? "All Subjects" : subject}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* Stats - Left side */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  {
                    icon: <BookOpen className="w-4 h-4 opacity-80" />,
                    label: "Total Books",
                    value: totalBooks,
                  },
                  {
                    icon: <Bookmark className="w-4 h-4 opacity-80" />,
                    label: "Subjects",
                    value: totalSubjects,
                  },
                  {
                    icon: <Clock className="w-4 h-4 opacity-80" />,
                    label: "Recent",
                    value: recentBooks,
                  },
                  {
                    icon: <User className="w-4 h-4 opacity-80" />,
                    label: "Authors",
                    value: totalAuthors,
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-2 bg-white/10 border border-white/30 backdrop-blur-sm rounded-md px-3 py-1 text-white text-xs font-medium whitespace-nowrap"
                  >
                    {item.icon}
                    <span>
                      {item.label}: <span className="font-bold">{item.value}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Search and Filter - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search books"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>{getFilterLabel()}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {getUniqueSubjects().map((subject) => (
                        <button
                          key={subject}
                          onClick={() => {
                            setFilterSubject(subject)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                            filterSubject === subject ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {subject === "all" ? "All Subjects" : subject}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-3 md:p-6">
            {/* Books Grid */}
            {books.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No books available</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Your teacher hasn't uploaded any books to the library yet. Check back later for new content.
                </p>
              </div>
            ) : filteredBooks.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching books found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setFilterSubject("all")
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredBooks.map((book) => (
                  <div
                    key={book._id}
                    className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col relative group max-w-xs mx-auto w-full cursor-pointer"
                    onClick={() => handleBookClick(book)}
                  >
                    {/* Book Header with Title */}
                    <div className="bg-gradient-to-r from-indigo-600 to-blue-600 h-16 flex items-center justify-between px-4 relative">
                      <h3 className="text-white font-semibold line-clamp-1 text-sm">{book.fileTitle}</h3>

                      {/* Three dots indicator (non-functional for students) */}

                    </div>

                    {/* Book Content */}
                    <div className="p-4 flex-grow flex flex-col">
                      <div className="mb-3">
                        <div className="flex items-center text-gray-600">
                          <User size={14} className="mr-1.5 text-gray-400" />
                          <p className="line-clamp-1 text-sm">{book.authorName}</p>
                        </div>
                      </div>

                      <div className="bg-indigo-50 px-3 py-1.5 rounded-full text-indigo-700 text-xs font-medium inline-flex items-center self-start mb-3">
                        <Bookmark size={12} className="mr-1" />
                        {book.fileSubject}
                      </div>

                      <div className="mt-auto space-y-1.5 pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center">
                            <Calendar size={12} className="mr-1.5" />
                            Added:
                          </span>
                          <span>{formatDate(book.createdAt)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center">
                            <Clock size={12} className="mr-1.5" />
                            Updated:
                          </span>
                          <span>{formatDate(book.updatedAt)}</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleBookClick(book)
                        }}
                        className="mt-3 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-md flex items-center justify-center gap-2 w-full text-sm"
                      >
                        <BookOpen size={16} />
                        <span>Open Study Buddy</span>
                        <ChevronRight size={14} className="ml-auto" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Books
